import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { drawerSections } from './content';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useMutation, useQuery } from '@apollo/client';
import { getDrawerData, setBusinessPartnerLineCommission } from './queries';
import { mapToDrawerData } from './utils';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { trimTrailingZeros } from '@utils/formatting-utils';
import PrecisionService from '@services/PrecisionService';
import {
  IDefaultCommissionDrawerProps,
  IDefaultCommissionInfo,
  IDefaultCommissionLovs,
} from '.';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ErrorsOf, IAbstractRecord } from '@models';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { MAIN_THEME } from '@styles';

const useStyles = makeStyles()(() => ({
  note: {
    color: 'red !important',
    fontSize: `${MAIN_THEME.fontSize.xs} !important`,
    fontStyle: 'italic',
  },
}));

const DefaultCommissionDrawer: React.FC<IDefaultCommissionDrawerProps> = ({
  assignedLineId,
  open,
  onSuccess,
  onClose,
}) => {
  const { classes } = useStyles();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [booted, setBooted] = useState<boolean>(false);

  const [values, setValues] = useState<IDefaultCommissionInfo>({
    assignedLineName: '',
    commissionType: '',
    lineCommissionValue: null,
    valueCurrency: '',
    lineCommissionRate: null,
    applicableOn: '',
  });
  const [lovs, setLovs] = useState<IDefaultCommissionLovs>({
    applicableOn: {},
    commissionType: {},
  });
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    drawerSections(values, lovs)
  );

  const { data: drawerResponse } = useQuery(getDrawerData, {
    variables: { id: assignedLineId },
    skip: !assignedLineId,
  });
  const [defaultCommissionAction] = useMutation(
    setBusinessPartnerLineCommission
  );

  const initialize = async () => {
    try {
      if (!drawerResponse) return;

      const { data, lovs } = mapToDrawerData(drawerResponse);

      if (!data || !lovs) return;

      const newSections = drawerSections(data, lovs);
      setLovs(lovs);
      setValues(data);
      setInputsForm(newSections);
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, [drawerResponse]);

  const submitForm = async (formValues: IAbstractRecord) => {
    const [data] = normalizeDynamicFormDrawerValues(
      drawerSections(formValues as unknown as IDefaultCommissionInfo, lovs),
      formValues
    );
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const isRate = data.commissionType === 'RATE';
      const variables = {
        lineCommissionInputs: {
          lineCommissionType: data.commissionType,
          lineCommissionValue: isRate
            ? null
            : trimTrailingZeros(data.lineCommissionValue),
          lineCommissionValueCurrency: isRate
            ? null
            : data.ValueCurrency || '39735',
          lineCommissionRate: !isRate
            ? 0
            : PrecisionService.divideBy100(data.lineCommissionRate),
          lineCommissionApplicableOn: isRate ? data.ApplicableOn : null,
        },
      };
      defaultCommissionAction({
        variables: { ...variables, entityId: assignedLineId },
        errorPolicy: 'all',
      }).then((res) => {
        if (isEmpty(res.errors)) {
          toast.success(
            <ToastSuccessMessage>
              Line default commissions successfully updated. Note that this
              change will not affect previously assigned plans.
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            setFormDisabled(false);
            setSubmitButtonState('success');
            onSuccess();
            onClose();
          }, 500);
        } else {
          setFormDisabled(false);
          setSubmitButtonState(undefined);
          toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        }
      });
    } catch {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const onChange = async (
    fieldName: keyof IDefaultCommissionInfo,
    value: IDefaultCommissionInfo[keyof IDefaultCommissionInfo],
    sectionIndex: number,
    newValues: IDefaultCommissionInfo,
    errors: ErrorsOf<IDefaultCommissionInfo>
  ) => {
    (values[fieldName] as typeof value) = value;

    if (fieldName === 'commissionType' && value === 'VALUE') {
      newValues.applicableOn = '';
      errors.applicableOn = '';
    }

    setValues(newValues);
  };

  return (
    <DynamicFormDrawer
      title="Set Default Commission"
      loading={!booted}
      onClose={onClose}
      isOpen={open}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
      noteSection={{
        className: classes.note,
        text: 'Note that changing the default commissions will only affect assigned plans in the future. This change will not affected previously assigned plans.',
      }}
    />
  );
};

export default DefaultCommissionDrawer;
